
    import { Component, Inject, Mixins } from 'vue-property-decorator'
    import Layout from '../../common/components/layout/layout.vue'
    import TotpSetupStepper from '@/app/settings/components/totp-setup-stepper.vue'
    import EmailSetupStepper from '@/app/settings/components/email-setup-stepper.vue'
    import MethodSelectionCard from '@/app/settings/components/method-selection-card.vue'
    import TwofactorEnabledCard from '@/app/settings/components/twofactor-enabled-card.vue'
    import LockoutOverlay from '@/app/settings/components/lockout-overlay.vue'
    import { LoadQrCommand } from '@/app/settings/commands/load-qr.command'
    import { ConfirmTwoFactorCommand } from '@/app/settings/commands/confirm-two-factor.command'
    import { SendEmailOtpCommand } from '@/app/auth/commands/send-email-otp.command'
    import { UpdateConsumerCommand } from '@/app/auth/commands/update-consumer.command'
    import { GetTwoFactorAuthStatusCommand } from '@/app/settings/commands/get-twofactor-auth-status.command'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import { TwoFactorRateLimitError } from '@/app/common/services/http/axios-client.service'
    import { mapState } from 'vuex'
    import { Consumer } from '@/app/identity'
    import {
        twoFactorSettingsStorage,
        TwoFactorMethod,
        TwoFactorStorage
    } from '@/app/auth/services/twofactor-storage.service'
    import { AddressStyleTranslationMixin } from '@/app/common/helpers/address-style-translation'
    import { TwoFactorLockoutMixin } from '@/app/common/helpers/twofactor-lockout.mixin'
    import { License } from '@/app/license/entities/license.entity'

    @Component({
        components: {
            Layout,
            TotpSetupStepper,
            EmailSetupStepper,
            MethodSelectionCard,
            TwofactorEnabledCard,
            LockoutOverlay
        },
        mixins: [AddressStyleTranslationMixin],
        computed: {
            ...mapState('auth', ['consumer']),
            ...mapState('license', ['license'])
        }
    })
    export default class TwoFactorSettings extends Mixins(TwoFactorLockoutMixin) {
        // Type declaration for mixin method
        $ta!: (key: string, values?: Record<string, unknown>) => string

        // Page-specific state
        initialLoading = true
        twoFactorEnabled = false
        currentMethod: TwoFactorMethod = 'totp'
        selectedMethod: TwoFactorMethod = 'totp'
        enableFlowStep = 0
        switchMode = false
        previousMethod: TwoFactorMethod | null = null
        totpStep = 1
        emailStep = 1
        loading = false
        qrCode = ''
        maskedEmail = ''
        resending = false
        noEmailError = false
        noEmailMessage = ''
        license!: License
        consumer!: Consumer

        // Override storage getter from mixin
        get lockoutStorage(): TwoFactorStorage {
            return twoFactorSettingsStorage
        }

        get allowEmailTwoFactor(): boolean {
            return this.license?.features?.allowEmailTwoFactor ?? false
        }

        get hasConsumerEmail(): boolean {
            return !!(this.consumer?.email && this.consumer.email.trim() !== '')
        }

        get isEmailMethodDisabled(): boolean {
            // Disabled if no email OR if in switch mode and already using email
            return !this.hasConsumerEmail || (this.switchMode && this.previousMethod === 'email')
        }

        get emailMethodNote(): string {
            if (!this.hasConsumerEmail) {
                return this.$t('page.twofactor.method.email_unavailable') as string
            }
            return this.$t('page.twofactor.method.email_note') as string
        }

        @Inject() loadQrCommand!: LoadQrCommand
        @Inject() confirmTwoFactorCommand!: ConfirmTwoFactorCommand
        @Inject() sendEmailOtpCommand!: SendEmailOtpCommand
        @Inject() updateConsumerCommand!: UpdateConsumerCommand
        @Inject() getTwoFactorAuthStatusCommand!: GetTwoFactorAuthStatusCommand
        @Inject() notificationDispatcher!: NotificationDispatcher

        async mounted() {
            try {
                await this.updateConsumerCommand.run()
            } catch {
                // Ignore refresh errors
            }
            this.twoFactorEnabled = this.consumer.twoFactor
            this.currentMethod = this.consumer.twoFactorMethod || 'totp'
            this.initialLoading = false

            // Check for switch mode from query parameter
            if (this.$route.query.action === 'switch') {
                this.switchMode = true
                // The previous method was the opposite of current (since 2FA was just disabled)
                // We can infer it from query or use a default
                this.previousMethod = (this.$route.query.from as TwoFactorMethod) || null
                // Clear the query params
                this.$router.replace({ query: {} })

                // Show success notification for identity confirmation
                await this.notificationDispatcher.success(this.$t('page.twofactor.switch.identity_confirmed') as string)
            }

            // Check lockout state: first sessionStorage, then database (using mixin method)
            await this.checkAndRestoreLockoutState(this.getTwoFactorAuthStatusCommand)
        }

        get currentMethodLabel(): string {
            return this.currentMethod === 'totp'
                ? (this.$t('page.twofactor.method.app_title') as string)
                : (this.$t('page.twofactor.method.email') as string)
        }

        // Override endLockout to redirect based on 2FA state
        endLockout() {
            // Reset lockout state and check if we were actually locked out
            if (!this.resetLockoutState()) return

            // Use $nextTick to ensure Vue processes the isLockedOut = false change
            // before we update the flow step or redirect
            this.$nextTick(() => {
                if (this.twoFactorEnabled) {
                    // 2FA is enabled - stay on this page showing the enabled state
                    return
                }
                // 2FA is disabled - go back to the method selection screen
                this.enableFlowStep = 0
                this.resetOtpDigits()
                this.qrCode = ''
                this.totpStep = 1
                this.emailStep = 1
            })
        }

        // Override onRateLimitExpired to go to method selection without a page reload
        onRateLimitExpired() {
            // Always reset the rate limit state first (idempotent - safe to call multiple times)
            const wasRateLimited = this.rateLimited

            // Clear the rate limit state immediately
            this.rateLimited = false
            this.rateLimitMessage = ''
            this.attemptsRemaining = this.maxAttempts
            this.lockoutStorage.removeRateLimit()

            // Only clear the rate limit interval, NOT the lockout interval
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
                this.countdownInterval = null
            }

            // Only do navigation/reset if we were actually rate-limited
            if (!wasRateLimited) return

            // Use $nextTick to ensure Vue processes state changes
            this.$nextTick(() => {
                if (this.twoFactorEnabled) {
                    // 2FA is enabled - stay on this page showing enabled state
                    return
                }
                // 2FA is disabled - go back to the method selection screen
                this.enableFlowStep = 0
                this.resetOtpDigits()
                this.qrCode = ''
                this.totpStep = 1
                this.emailStep = 1
            })
        }

        /**
         * Override focusOtpInput from TwoFactorLockoutMixin.
         * Called by handleIncorrectCode() in the mixin after clearing OTP digits.
         * This page has no direct otpInput ref — the OTP input is nested inside
         * stepper components, so we delegate focus through the stepper refs.
         */
        focusOtpInput() {
            const totpStepper = this.$refs.totpSetupStepper as TotpSetupStepper | undefined
            if (totpStepper) {
                totpStepper.focus()
                return
            }
            const emailStepper = this.$refs.emailSetupStepper as EmailSetupStepper | undefined
            if (emailStepper) {
                emailStepper.focus()
            }
        }

        get consumerMaskedEmail(): string {
            const email = this.consumer?.email
            if (!email) return ''
            const [localPart, domain] = email.split('@')
            if (!domain) return email
            const maskedLocal =
                localPart.length > 2
                    ? localPart[0] + '*'.repeat(localPart.length - 2) + localPart[localPart.length - 1]
                    : localPart[0] + '*'
            return `${maskedLocal}@${domain}`
        }

        selectMethod(method: TwoFactorMethod) {
            this.selectedMethod = method
            this.enableFlowStep = 1
            this.switchMode = false // Clear switch mode when starting setup
            this.previousMethod = null
            this.resetOtpDigits()
            this.codeError = ''
            this.codeErrorActive = false
            this.attemptsRemaining = this.maxAttempts
            this.isLockedOut = false
            this.cleanupLockoutIntervals()
            this.noEmailError = false
            this.noEmailMessage = ''

            if (method === 'totp') {
                this.totpStep = 1
            } else {
                this.emailStep = 1
            }
        }

        cancelSetup() {
            this.enableFlowStep = 0
            this.resetOtpDigits()
            this.codeError = ''
            this.codeErrorActive = false
            this.attemptsRemaining = this.maxAttempts
            this.isLockedOut = false
            this.cleanupLockoutIntervals()
            this.qrCode = ''
            this.totpStep = 1
            this.emailStep = 1
        }

        cancelSwitchMode() {
            this.switchMode = false
            this.previousMethod = null
            this.$router.push({ name: 'settings' })
        }

        async loadQrCode() {
            try {
                this.loading = true
                const qr = await this.loadQrCommand.run()
                this.qrCode = btoa(qr)
                this.totpStep = 2
            } catch (err) {
                await this.notificationDispatcher.error((err as Error).message)
            } finally {
                this.loading = false
            }
        }

        async sendEmailCode(isResend = false) {
            // Prevent double clicks
            if (isResend && this.resending) return
            if (!isResend && this.loading) return

            try {
                if (isResend) {
                    this.resending = true
                } else {
                    this.loading = true
                }
                this.noEmailError = false
                const result = await this.sendEmailOtpCommand.run(this.consumer.id)

                if (result.noEmail) {
                    this.noEmailError = true
                    this.noEmailMessage = result.message || 'No email address available'
                    return
                }

                this.rateLimited = false
                this.maskedEmail = result.maskedEmail || ''

                if (!isResend) {
                    this.emailStep = 2
                } else {
                    // Start cooldown timer after successful resend
                    const emailSetupStepper = this.$refs.emailSetupStepper as EmailSetupStepper
                    if (emailSetupStepper) {
                        emailSetupStepper.startResendCooldown()
                    }
                }
                this.resetOtpDigits()
            } catch (err) {
                if (err instanceof TwoFactorRateLimitError) {
                    this.setRateLimit(err.retryAfterMinutes, err.message)
                    return
                }
                await this.notificationDispatcher.error((err as Error).message)
            } finally {
                this.loading = false
                this.resending = false
            }
        }

        async resendEmailCode() {
            await this.sendEmailCode(true)
        }

        async confirmTwoFactor() {
            try {
                this.loading = true
                this.codeError = ''
                this.codeErrorActive = false
                await this.confirmTwoFactorCommand.run(this.confirmationCode, this.selectedMethod)

                this.twoFactorEnabled = true
                this.currentMethod = this.selectedMethod
                this.enableFlowStep = 0
                this.resetOtpDigits()
                this.attemptsRemaining = this.maxAttempts

                await this.notificationDispatcher.success(
                    this.$ta('page.twofactor.success.enabled_message', {
                        method: this.currentMethodLabel
                    }) as string
                )
            } catch (err) {
                // Check if this is a rate limit error from the backend
                if (err instanceof TwoFactorRateLimitError) {
                    // Backend has rate limited - use their lockout duration
                    this.lockoutDuration = err.retryAfterSeconds || err.retryAfterMinutes * 60
                    this.startLockout()
                    this.resetOtpDigits()
                    return
                }

                // Handle incorrect code using mixin method
                this.handleIncorrectCode()
            } finally {
                this.loading = false
            }
        }
    }
